import { OrganizationMember } from '@prisma/client'
import { Avatar, Flex, Typography } from 'antd'
import React from 'react'
import { AgentFacade } from '~/agents/facade/client'
import { OrganizationMemberFromContext } from '~/core/context/organization/hooks/useOrganizationMembers'
import { DesignSystem } from '~/core/designSystem'
import { useAppSession } from '~/features/appSession/client/hooks/useAppSession'
import { OrganizationType } from '~/features/organization/common'
import { OrganizationMemberClientShared } from '~/features/organizationMember/clientShared'
import { SocketClient } from '~/plugins/socket/client'
import { useConversation } from '../../hooks/useConversation'
import { Actions } from './components/Actions'
import { Background } from './components/Background'
import { Tabs } from './components/Tabs'
import { useData } from './hooks/useData'
import { useLogic } from './hooks/useLogic'

type Props = {
  organization: OrganizationType
  organizationMember: OrganizationMember
  organizationMembers: OrganizationMemberFromContext[]
  notificationCounters: Record<string, number>
  conversationId: string
  onRefreshOrganizationMembers: () => void
  onRefreshOrganization: () => void
}

export const HeaderV2: React.FC<Props> = ({
  organization,
  organizationMember,
  organizationMembers = [],
  conversationId,
  notificationCounters,
  onRefreshOrganizationMembers,
  onRefreshOrganization,
}) => {
  const data = useData({
    conversationId,
    organizationMember,
    organizationMembers,
  })

  const logic = useLogic()

  const designSystem = DesignSystem.use()

  const { clearConversation } = useConversation({
    conversationId,
    organizationMember,
  })

  const { setStatus } = useAppSession({
    appSession: data.appSession,
  })

  SocketClient.useEvent(
    `organization.${organization.id}.notification`,
    async _ => {
      onRefreshOrganization()
    },
  )

  const handleEdit = () => {
    logic.setSettingsVisible(true)
  }

  /* -------------------------------- COMPUTED -------------------------------- */

  const name = OrganizationMemberClientShared.Helper.getName(
    data.organizationMemberAgent,
  )

  const role = OrganizationMemberClientShared.Helper.getRole(
    data.organizationMemberAgent,
  )

  const pictureUrl = OrganizationMemberClientShared.Helper.getPictureUrl(
    data.organizationMemberAgent,
  )

  const classMobile = designSystem.isMobile ? 'pl-4' : ''

  return (
    <>
      <Background organizationMemberAgent={data.organizationMemberAgent}>
        {data.organizationMemberAgent && (
          <>
            <Flex className={`pr-4 pt-4 mb-4 ${classMobile}`}>
              <Flex gap={8} align="center">
                <Flex>
                  <Avatar src={pictureUrl} size={44} shape="square" />
                </Flex>

                <Flex vertical>
                  <Typography.Title level={4} style={{ margin: 0 }}>
                    {role}
                  </Typography.Title>
                  <Typography.Text
                    type="secondary"
                    style={{ margin: 0, fontWeight: 400 }}
                  >
                    {name}
                  </Typography.Text>
                </Flex>
              </Flex>

              <Flex flex={1} justify="end">
                <Actions
                  appSession={data.appSession}
                  onClear={() => clearConversation()}
                  onEdit={handleEdit}
                  onChangeStatus={status => setStatus(status)}
                />
              </Flex>
            </Flex>

            <Flex className={`pr-4 border-b border-gray-100 ${classMobile}`}>
              <Tabs
                app={data.app}
                appSession={data.appSession}
                conversationId={conversationId}
                organization={organization}
                organizationMemberAgent={data.organizationMemberAgent}
                notificationCounters={notificationCounters}
                onEdit={handleEdit}
              />
            </Flex>
          </>
        )}

        <span />
      </Background>

      {logic.isSettingsVisible && (
        <AgentFacade.Settings
          appKey={data.app.key}
          appSession={data.appSession}
          onSuccess={onRefreshOrganizationMembers}
          onClose={() => logic.setSettingsVisible(false)}
        />
      )}
    </>
  )
}
